/**
 * Preview: the player's own components against a version's content (plan
 * docs/plans/lms-plan.md 8.1 "Preview opens the player against the draft";
 * 18.22; WP-44; R10, R12).
 *
 * How a draft is previewed (confirmed in backend/src/services/lms/courses/
 * reads.js): the learner's GET /courses/:id/content serves a published or
 * superseded version only, never a draft, even to an admin with ?version_id=
 * ("an admin previews a draft through GET /admin/versions/:id"). So the
 * learn route cannot open a draft; this preview reads the admin's version
 * detail and renders it with the player's deck (buildDeck), its SlideView
 * and its ReadingStage, so the author sees exactly what a learner will. It
 * records nothing: no progress, no attempt, no "Mark as read".
 *
 * PreviewPage is the route element at
 * /lms/manage/catalog/versions/:versionId/preview; VersionPreview is also
 * the read-only body of a published version's page.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, Loader2 } from 'lucide-react';
import SegmentedControl from '../../../../ui/SegmentedControl';
import { outlineBtnCls, primaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { getVersion } from '../../../api/catalog';
import type { VersionDetail } from '../../../api/catalog';
import { buildDeck } from '../../../components/player/deck';
import { ReadingStage } from '../../../components/player/ReadingStage';
import { SlideView } from '../../../components/player/SlideView';
import { noticeCls } from '../../../components/player/styles';
import type { SlideAnswer } from '../../../hooks/usePlayer';
import { catalogPaths, STATUS_LABELS } from './model';
import { cardCls, linkCls, microLabelCls } from './styles';

type PreviewMode = 'slides' | 'reading';

export function VersionPreview({ detail }: { detail: VersionDetail }) {
  const { version, course } = detail;
  const content = detail.content;
  const [mode, setMode] = useState<PreviewMode>('slides');
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, SlideAnswer>>({});
  const [readingDone, setReadingDone] = useState(false);

  // A version after the first opens with its change summary, as a retrain shows it (7.2, 8.1).
  const changeSummary = version.version_no >= 2 ? version.change_summary : null;
  const deck = useMemo(() => buildDeck(content, { changeSummary }), [content, changeSummary]);
  const slides = deck.slides;
  const current = slides[Math.min(index, slides.length - 1)] ?? null;
  const section = current ? deck.sections[current.sectionIndex] : null;
  const answer = current ? answers[current.key] : undefined;

  const select = (optionId: string, multi: boolean) => {
    if (!current) return;
    setAnswers((all) => {
      const prior = all[current.key];
      if (prior?.checked) return all;
      const selected = prior?.selected ?? [];
      const next = multi ? (selected.includes(optionId) ? selected.filter((x) => x !== optionId) : [...selected, optionId]) : [optionId];
      return { ...all, [current.key]: { selected: next, checked: false } };
    });
  };

  return (
    <div className="space-y-4" data-testid="lms-version-preview">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedControl
          label="Preview view"
          options={[
            { value: 'slides', label: 'Slides' },
            { value: 'reading', label: 'Reading' },
          ]}
          value={mode}
          onChange={(v) => setMode(v as PreviewMode)}
        />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {`${course.title}, version ${version.version_no} (${STATUS_LABELS[version.status].toLowerCase()})`}
        </span>
      </div>

      {mode === 'slides' ? (
        slides.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">This version has no slides yet.</p>
        ) : (
          <div className={`${cardCls} space-y-4 sm:p-6`} data-testid="lms-preview-slides">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={microLabelCls}>{section ? section.title : ''}</span>
              <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400" data-testid="lms-preview-position">
                {`Slide ${Math.min(index, slides.length - 1) + 1} of ${slides.length}`}
              </span>
            </div>
            {current ? (
              <SlideView
                key={current.key}
                entry={current}
                changeSummary={changeSummary}
                versionNo={version.version_no}
                keyTakeaways={Array.isArray(content.key_takeaways) ? content.key_takeaways : []}
                answer={answer}
                onSelect={select}
                onCheck={() =>
                  setAnswers((all) => (all[current.key] ? { ...all, [current.key]: { ...all[current.key], checked: true } } : all))
                }
                onChoose={(optionId) => setAnswers((all) => ({ ...all, [current.key]: { selected: [optionId], checked: true } }))}
              />
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
              <button
                type="button"
                className={outlineBtnCls()}
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                disabled={index <= 0}
                data-testid="lms-preview-prev"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Previous
              </button>
              <button
                type="button"
                className={primaryBtnCls}
                onClick={() => setIndex((i) => Math.min(slides.length - 1, i + 1))}
                disabled={index >= slides.length - 1}
                data-testid="lms-preview-next"
              >
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )
      ) : (
        <ReadingStage
          courseTitle={course.title}
          versionNo={version.version_no}
          content={content}
          sources={Array.isArray(detail.sources) ? detail.sources : []}
          readingDone={readingDone}
          onMarkRead={async () => {
            setReadingDone(true);
            return true;
          }}
          acknowledgeFlow={course.kind === 'read_ack'}
          target={null}
          onTargetShown={() => {}}
          nextLabel={null}
          nextDisabledReason={null}
          onNext={() => {}}
          saveError={null}
        />
      )}
    </div>
  );
}

/** The route element: /lms/manage/catalog/versions/:versionId/preview. */
export function PreviewPage() {
  const { versionId } = useParams();
  const [detail, setDetail] = useState<VersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    if (!versionId) {
      setError('No version was named.');
      return undefined;
    }
    getVersion(versionId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'The version could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [versionId]);

  return (
    <div className="space-y-4" data-testid="lms-catalog-preview">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {versionId ? (
          <Link to={catalogPaths.version(versionId)} className={`inline-flex items-center gap-1 text-sm ${linkCls}`} data-testid="lms-preview-back">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to the editor
          </Link>
        ) : null}
      </div>
      <div className={noticeCls('primary')} role="note">
        <Eye className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>
          {detail && detail.version.status === 'draft'
            ? `Preview of draft version ${detail.version.version_no}, as learners will see it once it is published. Nothing you do here is recorded.`
            : 'Preview of this version, as learners see it. Nothing you do here is recorded.'}
        </span>
      </div>
      {error ? (
        <div className={noticeCls('red')} role="alert">
          {error}
        </div>
      ) : null}
      {!error && !detail ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading the preview...
        </div>
      ) : null}
      {detail ? <VersionPreview detail={detail} /> : null}
    </div>
  );
}

export default PreviewPage;
